import { useState, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import {
  FileCheck, Users, CheckCircle2, Clock, AlertTriangle, FileText,
  Calendar, Folder, TrendingUp, XCircle, Building2, FileSpreadsheet,
  Eye,
} from 'lucide-react';
import { useExportMetrics } from '@/hooks/useExportMetrics';
import { useDeliverablesMetrics } from '@/hooks/useDeliverablesMetrics';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend,
} from 'recharts';
import { useRole } from '@/contexts/RoleContext';
import { useOrgFilterStore } from '@/hooks/useOrgFilterStore';

const COLORS = ['hsl(var(--success))', 'hsl(var(--primary))', 'hsl(var(--warning))', 'hsl(var(--destructive))', 'hsl(var(--muted-foreground))'];

const STATUS_ICONS: Record<string, any> = {
  not_started: Clock,
  in_progress: AlertTriangle,
  submitted: FileCheck,
  validated: CheckCircle2,
  needs_revision: XCircle,
};

const STATUS_COLORS: Record<string, string> = {
  not_started: 'bg-muted text-muted-foreground',
  in_progress: 'bg-warning/20 text-warning',
  submitted: 'bg-primary/20 text-primary',
  validated: 'bg-success/20 text-success',
  needs_revision: 'bg-destructive/20 text-destructive',
};

export function DeliverablesMetricsPage() {
  const { currentRole } = useRole();
  const isSuperAdmin = currentRole === 'super_admin';
  const { exportToExcel, exportToPDF } = useExportMetrics();
  const { deliverables, organizations, isLoading, STATUS_LABELS } = useDeliverablesMetrics();

  const [organizationFilter, setOrganizationFilter] = useOrgFilterStore();
  const [userFilter, setUserFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [deliverableFilter, setDeliverableFilter] = useState<string>('all');
  const [selectedUser, setSelectedUser] = useState<{ name: string; deliverables: typeof deliverables } | null>(null);

  const orgFiltered = useMemo(() =>
    organizationFilter === 'all' ? deliverables : deliverables.filter(d => d.organization_id === organizationFilter),
    [deliverables, organizationFilter]
  );

  const users = useMemo(() => [...new Set(orgFiltered.map(d => d.user_name))].sort(), [orgFiltered]);

  const deliverableNames = useMemo(() => [...new Set(orgFiltered.map(d => d.name || d.workshop_title || '').filter(Boolean))].sort(), [orgFiltered]);

  const filtered = useMemo(() =>
    orgFiltered.filter(d => {
      if (userFilter !== 'all' && d.user_name !== userFilter) return false;
      if (statusFilter !== 'all' && d.status !== statusFilter) return false;
      if (deliverableFilter !== 'all') {
        const dName = d.name || d.workshop_title || '';
        if (dName !== deliverableFilter) return false;
      }
      return true;
    }),
    [orgFiltered, userFilter, statusFilter, deliverableFilter]
  );

  const stats = useMemo(() => ({
    total: orgFiltered.length,
    validated: orgFiltered.filter(d => d.status === 'validated').length,
    submitted: orgFiltered.filter(d => d.status === 'submitted').length,
    inProgress: orgFiltered.filter(d => d.status === 'in_progress').length,
    notStarted: orgFiltered.filter(d => d.status === 'not_started').length,
    needsRevision: orgFiltered.filter(d => d.status === 'needs_revision').length,
  }), [orgFiltered]);

  const statusChartData = useMemo(() => [
    { name: 'Validados', value: stats.validated, color: 'hsl(var(--success))' },
    { name: 'Enviados', value: stats.submitted, color: 'hsl(var(--primary))' },
    { name: 'En Progreso', value: stats.inProgress, color: 'hsl(var(--warning))' },
    { name: 'No Iniciados', value: stats.notStarted, color: 'hsl(var(--muted-foreground))' },
    { name: 'Revisión', value: stats.needsRevision, color: 'hsl(var(--destructive))' },
  ].filter(d => d.value > 0), [stats]);

  const userProgress = useMemo(() => {
    const byUser: Record<string, { total: number; validated: number; area: string | null }> = {};
    orgFiltered.forEach(d => {
      if (!byUser[d.user_name]) byUser[d.user_name] = { total: 0, validated: 0, area: d.area };
      byUser[d.user_name].total++;
      if (d.status === 'validated') byUser[d.user_name].validated++;
    });
    return Object.entries(byUser)
      .map(([name, data]) => ({
        name,
        area: data.area,
        total: data.total,
        validated: data.validated,
        progress: data.total > 0 ? Math.round((data.validated / data.total) * 100) : 0,
      }))
      .sort((a, b) => b.progress - a.progress);
  }, [orgFiltered]);

  const orgComparisonData = useMemo(() =>
    organizations.map(org => {
      const orgDel = deliverables.filter(d => d.organization_id === org.id);
      const validated = orgDel.filter(d => d.status === 'validated').length;
      const total = orgDel.length;
      return {
        name: org.name.split(' ')[0],
        fullName: org.name,
        total,
        validated,
        tasaValidacion: total > 0 ? Math.round((validated / total) * 100) : 0,
      };
    }).filter(o => o.total > 0),
    [organizations, deliverables]
  );

  const selectedOrgName = organizationFilter !== 'all'
    ? organizations.find(o => o.id === organizationFilter)?.name : 'Todas las organizaciones';

  const handleExportExcel = () => {
    exportToExcel(filtered.map(d => ({
      nombre: d.name || 'Sin nombre',
      usuario: d.user_name,
      estado: STATUS_LABELS[d.status] || d.status,
      fecha: d.submitted_at ? new Date(d.submitted_at).toLocaleDateString('es-MX') : 'N/A',
    })), [
      { key: 'nombre', header: 'Nombre' },
      { key: 'usuario', header: 'Usuario' },
      { key: 'estado', header: 'Estado' },
      { key: 'fecha', header: 'Fecha' },
    ], {
      fileName: `metricas-entregables-${new Date().toISOString().split('T')[0]}`,
      title: 'Métricas de Entregables',
    });
  };

  const handleExportPDF = async () => {
    const { default: jsPDF } = await import('jspdf');
    const { default: autoTable } = await import('jspdf-autotable');

    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const dateStr = new Date().toLocaleDateString('es-ES', { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' });

    // --- Header ---
    doc.setFontSize(20);
    doc.setTextColor(40, 40, 40);
    doc.text('Métricas de Entregables', 14, 22);
    doc.setFontSize(11);
    doc.setTextColor(100, 100, 100);
    doc.text(selectedOrgName || '', 14, 30);
    doc.setFontSize(9);
    doc.setTextColor(150, 150, 150);
    doc.text(`Generado: ${dateStr}`, 14, 36);

    // --- Resumen General ---
    let y = 46;
    doc.setFontSize(14);
    doc.setTextColor(40, 40, 40);
    doc.text('Resumen General', 14, y);
    y += 8;

    const summaryItems = [
      { label: 'Total Entregables', value: stats.total },
      { label: 'Validados', value: `${stats.validated} (${stats.total > 0 ? Math.round((stats.validated / stats.total) * 100) : 0}%)` },
      { label: 'Enviados', value: stats.submitted },
      { label: 'En Progreso', value: stats.inProgress },
      { label: 'No Iniciados', value: stats.notStarted },
      { label: 'Requieren Revisión', value: stats.needsRevision },
    ];
    summaryItems.forEach(item => {
      doc.setFontSize(10);
      doc.setTextColor(80, 80, 80);
      doc.text(`${item.label}:`, 14, y);
      doc.setTextColor(40, 40, 40);
      doc.text(String(item.value), 65, y);
      y += 6;
    });

    // --- Barras de estado ---
    y += 4;
    const barData = [
      { label: 'Validados', value: stats.validated, color: [34, 197, 94] as [number, number, number] },
      { label: 'Enviados', value: stats.submitted, color: [79, 70, 229] as [number, number, number] },
      { label: 'En Progreso', value: stats.inProgress, color: [245, 158, 11] as [number, number, number] },
      { label: 'No Iniciados', value: stats.notStarted, color: [156, 163, 175] as [number, number, number] },
      { label: 'Revisión', value: stats.needsRevision, color: [239, 68, 68] as [number, number, number] },
    ];
    const maxVal = Math.max(...barData.map(b => b.value), 1);
    const barMaxWidth = 100;
    barData.forEach(bar => {
      doc.setFontSize(8);
      doc.setTextColor(60, 60, 60);
      doc.text(bar.label, 14, y);
      doc.setFillColor(230, 230, 235);
      doc.roundedRect(50, y - 3.5, barMaxWidth, 5, 1, 1, 'F');
      const w = (bar.value / maxVal) * barMaxWidth;
      if (w > 0) {
        doc.setFillColor(bar.color[0], bar.color[1], bar.color[2]);
        doc.roundedRect(50, y - 3.5, w, 5, 1, 1, 'F');
      }
      doc.text(String(bar.value), 50 + barMaxWidth + 4, y);
      y += 8;
    });

    // --- Progreso por Participante ---
    y += 4;
    if (y > 220) { doc.addPage(); y = 20; }
    doc.setFontSize(14);
    doc.setTextColor(40, 40, 40);
    doc.text('Progreso por Participante', 14, y);
    y += 4;

    if (userProgress.length > 0) {
      const userTableData = userProgress.map(u => [
        u.name,
        u.area || '—',
        `${u.validated}/${u.total}`,
        `${u.progress}%`,
      ]);

      autoTable(doc, {
        head: [['Participante', 'Área', 'Validados/Total', 'Avance']],
        body: userTableData,
        startY: y,
        styles: { fontSize: 9, cellPadding: 2.5 },
        headStyles: { fillColor: [79, 70, 229], textColor: 255, fontStyle: 'bold' },
        columnStyles: {
          2: { halign: 'center' },
          3: { halign: 'center' },
        },
        alternateRowStyles: { fillColor: [245, 245, 250] },
        margin: { left: 14, right: 14 },
      });
      y = (doc as any).lastAutoTable.finalY + 10;
    }

    // --- Listado de Entregables ---
    if (y > 230) { doc.addPage(); y = 20; }
    doc.setFontSize(14);
    doc.setTextColor(40, 40, 40);
    doc.text('Listado de Entregables', 14, y);
    y += 4;

    if (filtered.length > 0) {
      const delTableData = filtered.map(d => [
        d.name || 'Sin nombre',
        d.user_name,
        STATUS_LABELS[d.status] || d.status,
        d.workshop_title ? `${d.workshop_title}${d.workshop_deleted ? ' (eliminado)' : ''}` : '—',
        d.submitted_at ? new Date(d.submitted_at).toLocaleDateString('es-MX') : '—',
      ]);

      autoTable(doc, {
        head: [['Entregable', 'Participante', 'Estado', 'Taller', 'Fecha']],
        body: delTableData,
        startY: y,
        styles: { fontSize: 8, cellPadding: 2 },
        headStyles: { fillColor: [79, 70, 229], textColor: 255, fontStyle: 'bold' },
        alternateRowStyles: { fillColor: [245, 245, 250] },
        margin: { left: 14, right: 14 },
      });
    }

    // --- Footer ---
    const pageCount = doc.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setTextColor(180, 180, 180);
      doc.text(`LIDER-IA — Métricas de Entregables — Página ${i} de ${pageCount}`, pageWidth / 2, doc.internal.pageSize.getHeight() - 8, { align: 'center' });
    }

    doc.save(`metricas-entregables-${new Date().toISOString().split('T')[0]}.pdf`);
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-64" />
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
          {[...Array(5)].map((_, i) => <Skeleton key={i} className="h-28" />)}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-display font-bold text-foreground">Métricas de Entregables</h1>
          <p className="text-muted-foreground">
            ¿Qué se ha entregado? {isSuperAdmin && organizationFilter !== 'all' && `- ${selectedOrgName}`}
          </p>
        </div>
        <div className="flex gap-3 flex-wrap">
          {isSuperAdmin && (
            <Select value={organizationFilter} onValueChange={setOrganizationFilter}>
              <SelectTrigger className="w-[200px]">
                <Building2 className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Organización" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas las organizaciones</SelectItem>
                {organizations.map(org => (
                  <SelectItem key={org.id} value={org.id}>{org.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <Button variant="outline" size="sm" onClick={handleExportExcel}>
            <FileSpreadsheet className="w-4 h-4 mr-2" />Excel
          </Button>
          <Button variant="outline" size="sm" onClick={handleExportPDF}>
            <FileText className="w-4 h-4 mr-2" />PDF
          </Button>
        </div>
      </div>

      {isSuperAdmin && organizationFilter === 'all' && orgComparisonData.length > 0 && (
        <Card className="glass-card border-accent/30">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Building2 className="w-5 h-5 text-accent" />
              Comparativa por Organización
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="h-[280px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={orgComparisonData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis dataKey="name" stroke="hsl(var(--muted-foreground))" />
                    <YAxis stroke="hsl(var(--muted-foreground))" />
                    <Tooltip contentStyle={{ backgroundColor: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: '8px' }} />
                    <Legend />
                    <Bar dataKey="total" name="Total" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="validated" name="Validados" fill="hsl(var(--success))" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <div className="space-y-4">
                <h4 className="font-semibold text-sm text-muted-foreground">Tasa de Validación</h4>
                {orgComparisonData.map(org => (
                  <div key={org.name} className="space-y-1">
                    <div className="flex justify-between text-sm">
                      <span>{org.fullName}</span>
                      <span className="font-bold text-success">{org.tasaValidacion}%</span>
                    </div>
                    <Progress value={org.tasaValidacion} className="h-2" />
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <Card className="glass-card border-accent/30">
          <CardContent className="p-6">
            <TrendingUp className="w-8 h-8 text-accent mb-2" />
            <p className="text-3xl font-bold text-accent">{stats.total}</p>
            <p className="text-sm text-muted-foreground">Total entregables</p>
          </CardContent>
        </Card>
        <Card className="glass-card border-success/30">
          <CardContent className="p-6">
            <CheckCircle2 className="w-8 h-8 text-success mb-2" />
            <p className="text-3xl font-bold text-success">{stats.validated}</p>
            <p className="text-sm text-muted-foreground">Validados</p>
          </CardContent>
        </Card>
        <Card className="glass-card border-primary/30">
          <CardContent className="p-6">
            <FileCheck className="w-8 h-8 text-primary mb-2" />
            <p className="text-3xl font-bold">{stats.submitted}</p>
            <p className="text-sm text-muted-foreground">Enviados</p>
          </CardContent>
        </Card>
        <Card className="glass-card border-warning/30">
          <CardContent className="p-6">
            <AlertTriangle className="w-8 h-8 text-warning mb-2" />
            <p className="text-3xl font-bold text-warning">{stats.inProgress}</p>
            <p className="text-sm text-muted-foreground">En progreso</p>
          </CardContent>
        </Card>
        <Card className="glass-card">
          <CardContent className="p-6">
            <Clock className="w-8 h-8 text-muted-foreground mb-2" />
            <p className="text-3xl font-bold">{stats.notStarted}</p>
            <p className="text-sm text-muted-foreground">No iniciados</p>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="glass-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-success" />
              Estado de Entregables
            </CardTitle>
          </CardHeader>
          <CardContent>
            {statusChartData.length === 0 ? (
              <p className="text-center text-muted-foreground py-8">Sin datos</p>
            ) : (
              <>
                <div className="h-[200px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie data={statusChartData} cx="50%" cy="50%" innerRadius={50} outerRadius={80} dataKey="value" nameKey="name" label={({ value }) => `${value}`}>
                        {statusChartData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip contentStyle={{ backgroundColor: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: '8px' }} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="flex justify-center gap-4 mt-2 flex-wrap">
                  {statusChartData.map(item => (
                    <div key={item.name} className="flex items-center gap-2 text-sm">
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }} />
                      <span>{item.name}: {item.value}</span>
                    </div>
                  ))}
                </div>
              </>
            )}
          </CardContent>
        </Card>

        <Card className="glass-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="w-5 h-5 text-primary" />
              Progreso por Usuario
            </CardTitle>
          </CardHeader>
          <CardContent>
            {userProgress.length === 0 ? (
              <p className="text-center text-muted-foreground py-8">Sin datos</p>
            ) : (
              <div className="space-y-3 max-h-[300px] overflow-y-auto">
                {userProgress.slice(0, 10).map(user => (
                  <div
                    key={user.name}
                    className="space-y-1 p-2 rounded-lg hover:bg-muted/50 cursor-pointer transition-colors"
                    onClick={() => {
                      const userDels = orgFiltered.filter(d => d.user_name === user.name);
                      setSelectedUser({ name: user.name, deliverables: userDels });
                    }}
                  >
                    <div className="flex justify-between text-sm">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{user.name}</span>
                        {user.area && <span className="text-muted-foreground text-xs">({user.area})</span>}
                        <Eye className="w-3 h-3 text-muted-foreground" />
                      </div>
                      <span className="font-bold">{user.validated}/{user.total}</span>
                    </div>
                    <Progress value={user.progress} className="h-2" />
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <Card className="glass-card">
        <CardHeader>
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <CardTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-secondary" />
                Listado de Entregables
              </CardTitle>
            </div>
            <div className="flex gap-2 flex-wrap">
              <Select value={deliverableFilter} onValueChange={setDeliverableFilter}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Entregable" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos los entregables</SelectItem>
                  {deliverableNames.map(n => <SelectItem key={n} value={n}>{n}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={userFilter} onValueChange={setUserFilter}>
                <SelectTrigger className="w-[160px]">
                  <SelectValue placeholder="Usuario" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  {users.map(u => <SelectItem key={u} value={u}>{u}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-[140px]">
                  <SelectValue placeholder="Estado" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  {Object.entries(STATUS_LABELS).map(([k, v]) => (
                    <SelectItem key={k} value={k}>{v}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {filtered.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">No hay entregables registrados</p>
          ) : (
            <div className="space-y-2">
              {filtered.slice(0, 20).map(d => {
                const StatusIcon = STATUS_ICONS[d.status] || Clock;
                return (
                  <div key={d.id} className="flex items-center justify-between p-3 bg-muted/30 rounded-lg hover:bg-muted/50 transition-colors">
                    <div className="flex items-center gap-3">
                      <div>
                        <span className="font-medium">{d.name || 'Entregable'}</span>
                        <p className="text-sm text-muted-foreground">
                          {d.user_name}
                          {d.workshop_title && ` • ${d.workshop_title}${d.workshop_deleted ? ' (eliminado)' : ''}`}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      {d.submitted_at && (
                        <div className="flex items-center gap-1 text-sm text-muted-foreground">
                          <Calendar className="w-4 h-4" />
                          {new Date(d.submitted_at).toLocaleDateString('es-MX')}
                        </div>
                      )}
                      <Badge className={STATUS_COLORS[d.status] || 'bg-muted text-muted-foreground'}>
                        <StatusIcon className="w-3 h-3 mr-1" />
                        {STATUS_LABELS[d.status] || d.status}
                      </Badge>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* User Detail Modal */}
      <Dialog open={!!selectedUser} onOpenChange={(open) => !open && setSelectedUser(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="w-5 h-5 text-primary" />
              Entregables de {selectedUser?.name}
            </DialogTitle>
          </DialogHeader>
          {selectedUser && (() => {
            const delivered = selectedUser.deliverables.filter(d => d.status === 'validated' || d.status === 'submitted');
            const pending = selectedUser.deliverables.filter(d => d.status !== 'validated' && d.status !== 'submitted');
            return (
              <div className="space-y-4">
                <div className="flex gap-4 text-sm">
                  <span>Total: <span className="font-bold">{selectedUser.deliverables.length}</span></span>
                  <span className="text-success">Entregados: <span className="font-bold">{delivered.length}</span></span>
                  <span className="text-warning">Pendientes: <span className="font-bold">{pending.length}</span></span>
                </div>
                <Progress value={selectedUser.deliverables.length > 0 ? Math.round((delivered.length / selectedUser.deliverables.length) * 100) : 0} className="h-2" />

                {delivered.length > 0 && (
                  <div>
                    <p className="text-sm font-medium text-success mb-2 flex items-center gap-1"><CheckCircle2 className="w-4 h-4" /> Entregados</p>
                    <Table>
                      <TableHeader><TableRow><TableHead>Entregable</TableHead><TableHead>Taller</TableHead><TableHead>Estado</TableHead><TableHead>Fecha</TableHead></TableRow></TableHeader>
                      <TableBody>
                        {delivered.map(d => (
                          <TableRow key={d.id}>
                            <TableCell className="font-medium">{d.name || 'Sin nombre'}</TableCell>
                            <TableCell className="text-sm text-muted-foreground">{d.workshop_title ? `${d.workshop_title}${d.workshop_deleted ? ' (eliminado)' : ''}` : '—'}</TableCell>
                            <TableCell><Badge className={STATUS_COLORS[d.status]}>{STATUS_LABELS[d.status]}</Badge></TableCell>
                            <TableCell className="text-sm text-muted-foreground">{d.submitted_at ? new Date(d.submitted_at).toLocaleDateString('es-MX') : '—'}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}

                {pending.length > 0 && (
                  <div>
                    <p className="text-sm font-medium text-warning mb-2 flex items-center gap-1"><Clock className="w-4 h-4" /> Pendientes</p>
                    <Table>
                      <TableHeader><TableRow><TableHead>Entregable</TableHead><TableHead>Taller</TableHead><TableHead>Estado</TableHead></TableRow></TableHeader>
                      <TableBody>
                        {pending.map(d => (
                          <TableRow key={d.id}>
                            <TableCell className="font-medium">{d.name || 'Sin nombre'}</TableCell>
                            <TableCell className="text-sm text-muted-foreground">{d.workshop_title ? `${d.workshop_title}${d.workshop_deleted ? ' (eliminado)' : ''}` : '—'}</TableCell>
                            <TableCell><Badge className={STATUS_COLORS[d.status]}>{STATUS_LABELS[d.status]}</Badge></TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>
    </div>
  );
}
